import { useCallback } from "react";
import pick from "lodash/pick";
import {
  IssueAttributes,
  Issue,
  IssueCustomAttributeMapping,
} from "@adsk/acc-issues-types";
import { useGetCustomFieldMappingsByIssueTypeId } from "../utils/hooks/issueCustomFieldMappingHooks";

// ensures that we are only changing the custom fields that
// actually should be edited!
export const pruneCustomFieldsOnEditedAttributes = ({
  editedAttributes,
  visibleFieldAttributeIds,
}: {
  editedAttributes: Partial<IssueAttributes>;
  visibleFieldAttributeIds: Set<string>;
}) => {
  if (!editedAttributes.customAttributes) {
    return editedAttributes;
  }
  const customAttributesToChange = editedAttributes.customAttributes.filter(
    customAttr => {
      // we ONLY want to edit custom fields that are CURRENTLY VISIBLE
      // and ARE NOT EMPTY. Otherwise we may either nullify existing data,
      // or make backend changes that the user did not expect to make.
      // (the latter case could have happened if a cusotm field was visible,
      // but then an additional issue was selected that did not have it and thus rendered it invisible)
      return (
        visibleFieldAttributeIds.has(customAttr.attributeDefinitionId) &&
        // 0 and false will never be the default, so we explicitly allow it,
        // but we never want to send the empty string, undefined, or null,
        // since those may be default values in the form
        (customAttr.value ||
          customAttr.value === 0 ||
          customAttr.value === false)
      );
    }
  );
  const prunedEditedAttributes: Partial<IssueAttributes> = {
    ...editedAttributes,
    customAttributes: customAttributesToChange,
  };
  if (!prunedEditedAttributes.customAttributes!.length) {
    delete prunedEditedAttributes.customAttributes;
  }
  return prunedEditedAttributes;
};

export const useGetVisibleBulkEditCustomFields = (): ((inputArg: {
  selectedTypeId?: string;
  selectedIssues: Issue[];
}) => IssueCustomAttributeMapping[]) => {
  const getCustomFieldMappingsByIssueTypeId =
    useGetCustomFieldMappingsByIssueTypeId();
  // get the intersection of the custom fields shared by all selected issues
  const getCustomFieldMappingsBySelectedIssues: (
    issues: Issue[]
    // eslint-disable-next-line react-hooks/rules-of-hooks
  ) => IssueCustomAttributeMapping[] = useCallback(
    selectedIssues => {
      const selectedIssuesTypes = new Set();
      let mappingsToMemoize: Record<string, IssueCustomAttributeMapping> = {};
      let isFirstType = true;
      selectedIssues.forEach((issue: Issue) => {
        selectedIssuesTypes.add(issue.issueTypeId);
      });
      selectedIssuesTypes.forEach(typeId => {
        const currentTypeCustomFieldMappings =
          getCustomFieldMappingsByIssueTypeId(typeId);
        if (isFirstType) {
          // since the result will be the intersection of types,
          // there will never be more custom fields than the first type has -- only less.
          // So we initialize `mappingsToMemoize` with the custom fields of the first type,
          // and as we iterate through the types we progressively take away any fields that are not shared
          currentTypeCustomFieldMappings.forEach(customFieldMapping => {
            mappingsToMemoize[customFieldMapping.attributeDefinitionId] =
              customFieldMapping;
          });
          isFirstType = false;
        } else {
          const customFieldIdsOfCurrentType =
            currentTypeCustomFieldMappings.map(
              customFieldMapping => customFieldMapping.attributeDefinitionId
            );
          // we get rid of any mappings that are not shared by all the current types
          // by using `pick` with the ids from each new type.
          mappingsToMemoize = pick(
            mappingsToMemoize,
            customFieldIdsOfCurrentType
          );
        }
      });
      return Object.values(mappingsToMemoize);
    },
    [getCustomFieldMappingsByIssueTypeId]
  );

  return useCallback(
    ({
      selectedTypeId,
      selectedIssues,
    }: {
      selectedTypeId?: string;
      selectedIssues: Issue[];
    }) => {
      // if there is a selected type, the visible custom fields are the fields of that type
      if (selectedTypeId) {
        return getCustomFieldMappingsByIssueTypeId(selectedTypeId);
      }
      // otherwise, it is the intersection of the custom fields shared by all selected issues
      return getCustomFieldMappingsBySelectedIssues(selectedIssues);
    },
    [
      getCustomFieldMappingsBySelectedIssues,
      getCustomFieldMappingsByIssueTypeId,
    ]
  );
};
